import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  X,
  AlertTriangle,
  Database,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Shield,
  Archive,
  RotateCcw,
  FileText,
} from 'lucide-react'
import {
  adminApi,
  type AdminOrganization,
  type DataResetOptions,
  type DataResetPreview,
  type DataResetResult,
  type BackupListItem,
  type BackupRestorePreview,
  type BackupRestoreResult,
} from '@/api/admin'

interface Props {
  organization: AdminOrganization
  onClose: () => void
}

type Step = 'options' | 'preview' | 'confirm' | 'executing' | 'result'
  | 'restore-list' | 'restore-preview' | 'restore-confirm' | 'restore-executing' | 'restore-result'

const ENV_STYLES: Record<string, { label: string; bg: string; text: string; border: string }> = {
  dev: { label: 'LOCAL / DEV', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  staging: { label: 'STAGING', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  prod: { label: 'PRODUCTION', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
}

export default function ResetDataModal({ organization, onClose }: Props) {
  const [step, setStep] = useState<Step>('options')
  const [options, setOptions] = useState<DataResetOptions>({
    keepMembers: false,
    keepLicenses: false,
    includeOrders: false,
    skipBackup: false,
  })
  const [preview, setPreview] = useState<DataResetPreview | null>(null)
  const [result, setResult] = useState<DataResetResult | null>(null)
  const [confirmInput, setConfirmInput] = useState('')
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set())

  // Restore states
  const [selectedBackup, setSelectedBackup] = useState<BackupListItem | null>(null)
  const [restorePreview, setRestorePreview] = useState<BackupRestorePreview | null>(null)
  const [restoreResult, setRestoreResult] = useState<BackupRestoreResult | null>(null)
  const [restoreConfirmInput, setRestoreConfirmInput] = useState('')

  const previewMutation = useMutation({
    mutationFn: () => adminApi.previewResetData(organization.id, options),
    onSuccess: (data) => {
      setPreview(data)
      setStep('preview')
      setExpandedPhases(new Set(data.phases.map((_, i) => i)))
    },
  })

  const executeMutation = useMutation({
    mutationFn: () => adminApi.executeResetData(organization.id, options),
    onSuccess: (data) => {
      setResult(data)
      setStep('result')
    },
  })

  const backupsQuery = useQuery({
    queryKey: ['backups', organization.id],
    queryFn: () => adminApi.listBackups(organization.id),
    enabled: step === 'restore-list',
  })

  const restorePreviewMutation = useMutation({
    mutationFn: (backupId: string) => adminApi.previewRestore(organization.id, backupId),
    onSuccess: (data) => {
      setRestorePreview(data)
      setStep('restore-preview')
    },
  })

  const restoreExecuteMutation = useMutation({
    mutationFn: (backupId: string) => adminApi.executeRestore(organization.id, backupId),
    onSuccess: (data) => {
      setRestoreResult(data)
      setStep('restore-result')
    },
  })

  const envStyle = preview
    ? ENV_STYLES[preview.environment] || ENV_STYLES.dev
    : ENV_STYLES.dev

  const requiredConfirmText = preview?.environment === 'prod'
    ? `CONFIRM-PROD-CLEAN-${organization.subdomain}`
    : preview?.environment === 'staging'
      ? organization.subdomain || ''
      : 'y'

  const isConfirmValid = confirmInput === requiredConfirmText

  const togglePhase = (idx: number) => {
    setExpandedPhases(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Database className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Reset Data</h2>
              <p className="text-sm text-slate-500">
                {organization.name} ({organization.subdomain})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {step === 'options' && (
              <button
                onClick={() => setStep('restore-list')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Restore
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step 1: Options */}
          {step === 'options' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-600">
                이 조직의 클라이언트 데이터를 초기화합니다. 실행 전 자동으로 백업이 생성됩니다.
              </p>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700">옵션</h3>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.keepMembers}
                    onChange={(e) => setOptions(prev => ({ ...prev, keepMembers: e.target.checked }))}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">멤버 보존</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      organization_members, departments, users를 삭제하지 않습니다
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.keepLicenses}
                    onChange={(e) => setOptions(prev => ({ ...prev, keepLicenses: e.target.checked }))}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">라이선스 보존</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      organization_licenses, organization_contracts를 삭제하지 않습니다
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.includeOrders}
                    onChange={(e) => setOptions(prev => ({ ...prev, includeOrders: e.target.checked }))}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">주문/결제도 삭제</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      orders, order_items, payments도 함께 삭제합니다 (기본: 보존)
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.skipBackup}
                    onChange={(e) => setOptions(prev => ({ ...prev, skipBackup: e.target.checked }))}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">백업 건너뛰기</span>
                    <p className="text-xs text-slate-500 mt-0.5 text-red-500">
                      삭제 전 자동 백업을 생성하지 않습니다 (비권장)
                    </p>
                  </div>
                </label>
              </div>

              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-700 mb-1">📁 백업 저장 위치</p>
                <p className="text-xs text-blue-600 font-mono">
                  Local: <span className="font-semibold">./backups/</span> (프로젝트 루트)
                </p>
                <p className="text-xs text-blue-600 font-mono">
                  Staging/Prod: <span className="font-semibold">S3 quebon-contents/data-reset-backups/</span>
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  파일명: {'{subdomain}_{yyyyMMdd_HHmmss}_{env}.json'}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Environment badge */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${envStyle.bg} ${envStyle.border}`}>
                <Shield className={`w-4 h-4 ${envStyle.text}`} />
                <span className={`text-sm font-semibold ${envStyle.text}`}>
                  환경: {envStyle.label}
                </span>
              </div>

              {/* Warning banners */}
              {preview.environment === 'prod' && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-700">PRODUCTION 데이터를 수정합니다!</p>
                    <p className="text-xs text-red-600 mt-1">이 작업은 되돌릴 수 없습니다. 반드시 확인 후 진행하세요.</p>
                  </div>
                </div>
              )}
              {preview.environment === 'staging' && (
                <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-700">STAGING 데이터를 수정합니다</p>
                    <p className="text-xs text-yellow-600 mt-1">테스트 환경 데이터가 삭제됩니다.</p>
                  </div>
                </div>
              )}

              {/* Phase counts */}
              <div className="space-y-2">
                {preview.phases.map((phase, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => togglePhase(idx)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
                    >
                      <div className="flex items-center gap-2">
                        {expandedPhases.has(idx)
                          ? <ChevronDown className="w-4 h-4 text-slate-400" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />
                        }
                        <span className="text-sm font-medium text-slate-700">{phase.phase}</span>
                      </div>
                      {phase.skipped ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-500">
                          SKIP: {phase.skipReason}
                        </span>
                      ) : (
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                          {phase.tables.reduce((sum, t) => sum + t.count, 0)} rows
                        </span>
                      )}
                    </button>
                    {expandedPhases.has(idx) && !phase.skipped && phase.tables.length > 0 && (
                      <div className="px-4 py-2 border-t border-slate-100">
                        <table className="w-full text-sm">
                          <tbody>
                            {phase.tables.map((t) => (
                              <tr key={t.tableName} className="border-b border-slate-50 last:border-0">
                                <td className="py-1.5 text-slate-600 font-mono text-xs">{t.tableName}</td>
                                <td className="py-1.5 text-right font-mono text-xs text-slate-900">{t.count.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
                <span className="text-sm font-semibold text-orange-700">총 영향받는 데이터</span>
                <span className="text-lg font-bold font-mono text-orange-700">
                  {preview.totalRows.toLocaleString()} rows
                </span>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && preview && (
            <div className="space-y-4">
              <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                preview.environment === 'prod' ? 'bg-red-50 border-red-200' :
                preview.environment === 'staging' ? 'bg-yellow-50 border-yellow-200' :
                'bg-blue-50 border-blue-200'
              }`}>
                <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  preview.environment === 'prod' ? 'text-red-500' :
                  preview.environment === 'staging' ? 'text-yellow-500' :
                  'text-blue-500'
                }`} />
                <div>
                  <p className={`text-sm font-semibold ${
                    preview.environment === 'prod' ? 'text-red-700' :
                    preview.environment === 'staging' ? 'text-yellow-700' :
                    'text-blue-700'
                  }`}>
                    {preview.totalRows.toLocaleString()}개의 데이터가 삭제됩니다
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    환경: <strong>{envStyle.label}</strong> | 조직: <strong>{organization.subdomain}</strong>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  {preview.environment === 'prod'
                    ? '확인을 위해 아래 문구를 정확히 입력하세요:'
                    : preview.environment === 'staging'
                      ? '확인을 위해 조직 subdomain을 입력하세요:'
                      : '확인을 위해 "y"를 입력하세요:'}
                </label>
                <p className="text-xs font-mono bg-slate-100 px-3 py-2 rounded border border-slate-200 text-slate-700 select-all">
                  {requiredConfirmText}
                </p>
                <input
                  type="text"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder="여기에 입력..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono"
                  autoFocus
                />
              </div>

              {!options.skipBackup && (
                <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                  <Archive className="w-4 h-4" />
                  실행 전 자동 백업이 생성됩니다
                </div>
              )}
            </div>
          )}

          {/* Step 4: Executing */}
          {step === 'executing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
              <p className="mt-4 text-sm font-medium text-slate-700">데이터 초기화 중...</p>
              <p className="text-xs text-slate-500 mt-1">잠시 기다려주세요</p>
            </div>
          )}

          {/* Step 5: Result */}
          {step === 'result' && result && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-700">데이터 초기화 완료</p>
                  <p className="text-xs text-green-600 mt-1">
                    총 {result.totalDeleted.toLocaleString()}개 row 삭제됨
                  </p>
                </div>
              </div>

              {/* Backup info */}
              {result.backupInfo && (
                <div className="space-y-1.5 text-xs bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Archive className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-blue-700">
                      백업 생성됨: <span className="font-mono">{result.backupInfo.backupId}.json</span>
                      {' '}({result.backupInfo.rowCount.toLocaleString()} rows)
                    </span>
                  </div>
                  {result.backupInfo.s3Location && (
                    <div className="ml-6 text-blue-600 font-mono truncate" title={result.backupInfo.s3Location}>
                      S3: {result.backupInfo.s3Location}
                    </div>
                  )}
                  {result.backupInfo.localPath && !result.backupInfo.s3Location && (
                    <div className="ml-6 text-blue-600 font-mono truncate" title={result.backupInfo.localPath}>
                      Local: {result.backupInfo.localPath}
                    </div>
                  )}
                </div>
              )}

              {/* Deleted counts */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <span className="text-sm font-medium text-slate-700">삭제된 테이블별 row 수</span>
                </div>
                <div className="px-4 py-2">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(result.deletedCounts)
                        .filter(([, count]) => count > 0)
                        .map(([table, count]) => (
                          <tr key={table} className="border-b border-slate-50 last:border-0">
                            <td className="py-1.5 text-slate-600 font-mono text-xs">{table}</td>
                            <td className="py-1.5 text-right font-mono text-xs text-green-700">
                              -{count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {Object.values(result.deletedCounts).every(c => c === 0) && (
                    <p className="text-xs text-slate-500 py-2 text-center">삭제된 데이터 없음</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* Restore Flow */}
          {/* ============================================================ */}

          {/* Restore Step 1: Backup List */}
          {step === 'restore-list' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <RotateCcw className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">백업에서 데이터 복원</span>
              </div>

              {backupsQuery.isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              )}

              {backupsQuery.data && backupsQuery.data.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">
                  이 조직의 백업 파일이 없습니다.
                </p>
              )}

              {backupsQuery.data && backupsQuery.data.length > 0 && (
                <div className="space-y-2">
                  {backupsQuery.data.map((backup) => (
                    <button
                      key={backup.backupId}
                      onClick={() => {
                        setSelectedBackup(backup)
                        restorePreviewMutation.mutate(backup.backupId)
                      }}
                      disabled={restorePreviewMutation.isPending}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 text-left transition-colors disabled:opacity-50"
                    >
                      <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{backup.fileName}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {backup.createdAt && (
                            <span className="text-xs text-slate-500">{backup.createdAt}</span>
                          )}
                          {backup.environment && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              backup.environment === 'prod' ? 'bg-red-100 text-red-700' :
                              backup.environment === 'staging' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {backup.environment}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            {(backup.fileSize / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </div>
                      {restorePreviewMutation.isPending && selectedBackup?.backupId === backup.backupId && (
                        <Loader2 className="w-4 h-4 text-emerald-500 animate-spin flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Restore Step 2: Preview */}
          {step === 'restore-preview' && restorePreview && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <RotateCcw className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">
                  복원 미리보기: <span className="font-mono">{restorePreview.backupId}</span>
                </span>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <span className="text-sm font-medium text-slate-700">복원될 테이블별 row 수</span>
                </div>
                <div className="px-4 py-2">
                  <table className="w-full text-sm">
                    <tbody>
                      {restorePreview.tables.map((t) => (
                        <tr key={t.tableName} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 text-slate-600 font-mono text-xs">{t.tableName}</td>
                          <td className="py-1.5 text-right font-mono text-xs text-emerald-700">
                            +{t.rowCount.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <span className="text-sm font-semibold text-emerald-700">총 복원 데이터</span>
                <span className="text-lg font-bold font-mono text-emerald-700">
                  {restorePreview.totalRows.toLocaleString()} rows
                </span>
              </div>

              <p className="text-xs text-slate-500">
                중복 데이터는 자동으로 무시됩니다 (ON CONFLICT DO NOTHING).
              </p>
            </div>
          )}

          {/* Restore Step 3: Confirm */}
          {step === 'restore-confirm' && restorePreview && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <RotateCcw className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-700">
                    {restorePreview.totalRows.toLocaleString()}개의 데이터를 복원합니다
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    백업: <strong className="font-mono">{restorePreview.backupId}</strong>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  확인을 위해 "restore"를 입력하세요:
                </label>
                <input
                  type="text"
                  value={restoreConfirmInput}
                  onChange={(e) => setRestoreConfirmInput(e.target.value)}
                  placeholder="restore"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Restore Step 4: Executing */}
          {step === 'restore-executing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
              <p className="mt-4 text-sm font-medium text-slate-700">데이터 복원 중...</p>
              <p className="text-xs text-slate-500 mt-1">잠시 기다려주세요</p>
            </div>
          )}

          {/* Restore Step 5: Result */}
          {step === 'restore-result' && restoreResult && (
            <div className="space-y-4">
              <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                restoreResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                {restoreResult.success
                  ? <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  : <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                }
                <div>
                  <p className={`text-sm font-semibold ${restoreResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {restoreResult.success ? '데이터 복원 완료' : '복원 중 일부 오류 발생'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    총 {restoreResult.totalRestored.toLocaleString()}개 row 복원됨
                  </p>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <span className="text-sm font-medium text-slate-700">복원된 테이블별 row 수</span>
                </div>
                <div className="px-4 py-2">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(restoreResult.restoredCounts)
                        .filter(([, count]) => count > 0)
                        .map(([table, count]) => (
                          <tr key={table} className="border-b border-slate-50 last:border-0">
                            <td className="py-1.5 text-slate-600 font-mono text-xs">{table}</td>
                            <td className="py-1.5 text-right font-mono text-xs text-emerald-700">
                              +{count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {Object.values(restoreResult.restoredCounts).every(c => c === 0) && (
                    <p className="text-xs text-slate-500 py-2 text-center">복원된 데이터 없음 (이미 존재)</p>
                  )}
                </div>
              </div>

              {restoreResult.errors.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-red-50 border-b border-red-200">
                    <span className="text-sm font-medium text-red-700">오류 ({restoreResult.errors.length})</span>
                  </div>
                  <div className="px-4 py-2 max-h-32 overflow-y-auto">
                    {restoreResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600 font-mono py-0.5">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          {step === 'options' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                취소
              </button>
              <button
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50"
              >
                {previewMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                미리보기
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => setStep('options')}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                옵션으로 돌아가기
              </button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                  취소
                </button>
                {preview && preview.totalRows > 0 && (
                  <button
                    onClick={() => { setStep('confirm'); setConfirmInput('') }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    실행 진행
                  </button>
                )}
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button
                onClick={() => setStep('preview')}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                뒤로
              </button>
              <button
                onClick={() => { setStep('executing'); executeMutation.mutate() }}
                disabled={!isConfirmValid || executeMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                초기화 실행
              </button>
            </>
          )}

          {step === 'executing' && (
            <div className="w-full text-center text-xs text-slate-500">처리 중...</div>
          )}

          {step === 'result' && (
            <>
              <div />
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-lg"
              >
                닫기
              </button>
            </>
          )}

          {/* Restore flow footers */}
          {step === 'restore-list' && (
            <>
              <button
                onClick={() => setStep('options')}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Reset으로 돌아가기
              </button>
              <div />
            </>
          )}

          {step === 'restore-preview' && (
            <>
              <button
                onClick={() => setStep('restore-list')}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                목록으로 돌아가기
              </button>
              <button
                onClick={() => { setStep('restore-confirm'); setRestoreConfirmInput('') }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg"
              >
                <RotateCcw className="w-4 h-4" />
                복원 진행
              </button>
            </>
          )}

          {step === 'restore-confirm' && restorePreview && (
            <>
              <button
                onClick={() => setStep('restore-preview')}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                뒤로
              </button>
              <button
                onClick={() => {
                  setStep('restore-executing')
                  restoreExecuteMutation.mutate(restorePreview.backupId)
                }}
                disabled={restoreConfirmInput !== 'restore' || restoreExecuteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                복원 실행
              </button>
            </>
          )}

          {step === 'restore-executing' && (
            <div className="w-full text-center text-xs text-slate-500">복원 처리 중...</div>
          )}

          {step === 'restore-result' && (
            <>
              <div />
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-lg"
              >
                닫기
              </button>
            </>
          )}
        </div>

        {/* Error display */}
        {(previewMutation.isError || executeMutation.isError || restorePreviewMutation.isError || restoreExecuteMutation.isError) && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200 text-sm text-red-700">
            {(previewMutation.error || executeMutation.error || restorePreviewMutation.error || restoreExecuteMutation.error)?.message || '오류가 발생했습니다'}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
